/**
 * Utility functions for booking status and monitoring
 */

/**
 * Maps monitoring recency to a traffic-light status. The 6h/24h
 * thresholds align with the twice-daily cron schedule (8am/8pm UTC).
 */
export function getMonitoringStatus(lastChecked: string | null, isActive: boolean): {
  text: string;
  color: 'green' | 'yellow' | 'gray';
} {
  if (!isActive) {
    return { text: 'Tracking paused', color: 'gray' };
  }

  if (!lastChecked) {
    return { text: 'First check pending', color: 'yellow' };
  }

  const hoursSince = (Date.now() - new Date(lastChecked).getTime()) / (1000 * 60 * 60);

  if (hoursSince < 6) {
    return { text: `Checked ${Math.floor(hoursSince)}h ago ✓`, color: 'green' };
  } else if (hoursSince < 24) {
    return { text: 'Check coming soon', color: 'yellow' };
  } else {
    return { text: 'Check overdue', color: 'yellow' };
  }
}

/**
 * Price trend indicator with a 2% dead zone to avoid showing negligible
 * fluctuations as "cheaper" or "higher" (airline pricing jitter).
 */
export function getPriceTrend(
  currentPrice: number | null,
  pricePaid: number,
): { emoji: string; text: string; color: string } {
  if (currentPrice === null) {
    return { emoji: '👀', text: 'Watching', color: 'stone-600' };
  }

  const diff = pricePaid - currentPrice;
  const percentDiff = (diff / pricePaid) * 100;

  if (Math.abs(percentDiff) < 2) {
    return { emoji: '➡️', text: 'Stable', color: 'stone-600' };
  } else if (diff > 0) {
    return { emoji: '📉', text: `${Math.abs(percentDiff).toFixed(0)}% cheaper`, color: 'forest' };
  } else {
    return { emoji: '📈', text: `${Math.abs(percentDiff).toFixed(0)}% higher`, color: 'sunset' };
  }
}

/**
 * Get award availability status for award bookings
 * Note: "no availability" is NOT an error - it's just the current state
 */
export function getAwardAvailabilityStatus(
  awardAvailable: boolean | null | undefined,
  lastChecked: string | null | undefined
): { emoji: string; text: string; color: 'green' | 'amber' | 'gray'; description: string } {
  // If we haven't checked yet
  if (lastChecked === null || lastChecked === undefined) {
    return {
      emoji: '👀',
      text: 'First check pending',
      color: 'gray',
      description: 'We\'ll check for award space soon'
    };
  }

  // If award_available is not set (null/undefined), we don't have data yet
  if (awardAvailable === null || awardAvailable === undefined) {
    return {
      emoji: '🔍',
      text: 'Searching',
      color: 'gray',
      description: 'Looking for award availability'
    };
  }

  // Award space is available - this is the exciting news!
  if (awardAvailable) {
    return {
      emoji: '✨',
      text: 'Award found!',
      color: 'green',
      description: 'Award space is available for rebooking'
    };
  }

  // Award space is NOT available - neutral state, keep watching
  // Gray (not amber): this is routine monitoring, not attention-worthy
  return {
    emoji: '⏳',
    text: 'No space yet',
    color: 'gray',
    description: 'We\'re watching for award availability'
  };
}

/**
 * Interprets price trend for the chart header. Returns a label + color.
 * Rising prices = good for the user (they paid less), so never amber.
 */
export function interpretPriceTrend(
  currentPrice: number | null | undefined,
  pricePaid: number,
): { label: string; color: 'emerald' | 'stone' } | null {
  if (currentPrice === null || currentPrice === undefined) return null;

  const diff = pricePaid - currentPrice;
  const pctDiff = (diff / pricePaid) * 100;

  if (Math.abs(pctDiff) < 2) {
    return { label: 'Prices are holding near what you paid.', color: 'stone' };
  }
  if (diff > 0) {
    return { label: `Prices have dipped ${Math.abs(pctDiff).toFixed(0)}% below your fare.`, color: 'emerald' };
  }
  return { label: `Current fares are ${Math.abs(pctDiff).toFixed(0)}% above your fare. You got a good deal.`, color: 'stone' };
}

/**
 * Interprets the latest price relative to what the user paid.
 * Returns a human-readable conclusion so the UI shows answers, not raw data.
 * Uses the same 2% dead zone as getPriceTrend.
 */
export function interpretPriceStatus(
  latestPrice: number | null | undefined,
  pricePaid: number,
): { message: string } | null {
  if (latestPrice === null || latestPrice === undefined) return null;

  const diff = pricePaid - latestPrice;
  const pctDiff = (diff / pricePaid) * 100;

  if (Math.abs(pctDiff) < 2) {
    return { message: 'Current fares are holding near what you paid.' };
  }
  if (diff > 0) {
    return { message: 'Prices have dipped slightly — we\'re watching for a bigger drop.' };
  }
  return { message: 'Current fares are above what you paid.' };
}
